/**
 * Service for generating conversation titles using AI
 */

interface TitleGenerationResponse {
  title: string;
  originalMessage?: string;
  fallback?: boolean;
  error?: string;
}

export const titleGenerationService = {
  /**
   * Generate a title for a conversation based on the first user message
   */
  async generateTitle(message: string): Promise<{
    data: string | null;
    error: Error | null;
  }> {
    try {
      if (!message || message.trim().length === 0) {
        return {
          data: null,
          error: new Error("Message is required for title generation"),
        };
      }

      console.log(
        "[TitleService] Generating title for message:",
        message.substring(0, 50) + "...",
      );

      // Use relative path - works for same domain deployment
      const response = await fetch("/api/generate-title", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        throw new Error(`Title generation failed: ${response.statusText}`);
      }

      const result: TitleGenerationResponse = await response.json();

      if (result.fallback) {
        console.warn(
          "[TitleService] Using fallback title generation:",
          result.error,
        );
      }

      console.log("[TitleService] Generated title:", result.title);

      return {
        data: result.title || "New conversation",
        error: null,
      };
    } catch (error) {
      console.error("[TitleService] Failed to generate title:", error);

      // Generate a simple fallback title locally
      const fallbackTitle = this.generateFallbackTitle(message);

      return {
        data: fallbackTitle,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Generate a simple fallback title locally if API fails
   */
  generateFallbackTitle(message: string): string {
    if (!message || message.trim().length === 0) {
      return "New conversation";
    }

    // Clean and truncate message
    const cleaned = message
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const words = cleaned.split(" ").filter((word) => word.length > 2);

    if (words.length === 0) {
      return "New conversation";
    }

    // Take first 3-5 meaningful words
    const titleWords = words.slice(0, Math.min(5, words.length));
    let title = titleWords.join(" ");

    // Ensure it's not too long
    if (title.length > 40) {
      title = title.substring(0, 37) + "...";
    }

    return title || "New conversation";
  },

  /**
   * Check if a message is the first user message in a conversation
   */
  isFirstUserMessage(messages: any[]): boolean {
    if (!messages || messages.length === 0) {
      return true;
    }

    // Count user messages only
    const userMessages = messages.filter((msg) => msg.role === "user");
    return userMessages.length === 1;
  },
};
